/**
 * Global upload-queue Zustand store.
 *
 * Passive observer: does NOT orchestrate the 3-step S3 chain. Each caller
 * (wizard / folder / library) still owns its own `request → upload → confirm`
 * Promise; this store receives lifecycle signals via `enqueue`, `updateProgress`,
 * `markSuccess`, `markFailed`, `markCancelled`.
 *
 * Non-serializable refs (AbortController, dismiss timer) live in
 * `./side-table.ts` to keep snapshots clean and avoid spurious re-renders.
 *
 * Action factories (`makeQueueActions`, `makeStatusActions`,
 * `makeDismissActions`) mirror the proven knowledge-bases store precedent
 * for readability and per-concern testability.
 */
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { computeProgress } from '../lib/progress';
import { sideTable } from './side-table';
import type {
  DismissActions,
  EnqueueInput,
  InternalActions,
  QueueActions,
  StatusActions,
  UploadItem,
  UploadQueueState,
  UploadQueueStore,
  UploadStatus,
} from './upload-queue.store.types';

const SUCCESS_DISMISS_MS = 3000;
const CANCELLED_DISMISS_MS = 1000;

/**
 * Terminal states are sinks. Once an item reaches `success | failed
 * | cancelled`, no late axios callback or duplicate user-action may rewrite
 * its status or progress. Guards in `markSuccess`, `markFailed`,
 * `markCancelled`, and `updateProgress` short-circuit on this set.
 */
const TERMINAL_STATES: ReadonlyArray<UploadStatus> = ['success', 'failed', 'cancelled'];

const initialState: UploadQueueState = { uploads: {} };

type SetState = (
  partial:
    | UploadQueueStore
    | Partial<UploadQueueStore>
    | ((state: UploadQueueStore) => UploadQueueStore | Partial<UploadQueueStore>),
  replace?: boolean,
) => void;
type GetState = () => UploadQueueStore;

function makeQueueActions(set: SetState): QueueActions {
  return {
    enqueue: (input: EnqueueInput): string => {
      const id = `upload_${uuidv4()}`;
      const controller = new AbortController();
      sideTable.attach(id, { abortController: controller, dismissTimeout: null });

      const item: UploadItem = {
        id,
        fileName: input.file.name,
        fileSize: input.file.size,
        mimeType: input.file.type,
        source: input.source,
        projectId: input.projectId,
        folderId: input.folderId,
        status: 'queued',
        progress: 0,
        loadedBytes: 0,
        totalBytes: input.file.size,
        startedAt: Date.now(),
      };

      set((state) => ({ uploads: { ...state.uploads, [id]: item } }));
      return id;
    },

    beginUploading: (id: string): void => {
      set((state) => {
        const item = state.uploads[id];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- idempotent guard on unknown id (see updateProgress).
        if (!item || item.status !== 'queued') return state;
        return { uploads: { ...state.uploads, [id]: { ...item, status: 'uploading' } } };
      });
    },

    updateProgress: (id: string, loaded: number, total: number): void => {
      set((state) => {
        const item = state.uploads[id];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- idempotent guard against late callbacks on unknown id; TS doesn't model the runtime absence (`noUncheckedIndexedAccess` is off).
        if (!item) return state;
        // Terminal guard: drop late progress callbacks that race past
        // markSuccess/markFailed/markCancelled. Without this, axios can still
        // emit a final `loaded === totalBytes` event after the user cancels
        // and rewind the row from `cancelled` back into a progress display.
        if (TERMINAL_STATES.includes(item.status)) return state;
        // Progress is monotonically non-decreasing during uploading. `loaded` is
        // the monotonic axis (bytes actually shipped). If `loaded` would
        // regress, drop the event — late / out-of-order progress must not
        // visibly rewind the bar.
        if (loaded < item.loadedBytes) {
          // Promote queued → uploading even when we drop the value (any
          // event, even a stale one, signals the request is in flight).
          if (item.status === 'queued') {
            return {
              uploads: { ...state.uploads, [id]: { ...item, status: 'uploading' } },
            };
          }
          return state;
        }
        // eslint-disable-next-line i18next/no-literal-string -- upload queue status values
        const nextStatus = item.status === 'queued' ? 'uploading' : item.status;
        return {
          uploads: {
            ...state.uploads,
            [id]: {
              ...item,
              loadedBytes: loaded,
              totalBytes: total,
              progress: computeProgress(loaded, total),
              status: nextStatus,
            },
          },
        };
      });
    },
  };
}

function makeStatusActions(set: SetState, get: GetState): StatusActions {
  return {
    markSuccess: (id: string): void => {
      let transitioned = false;
      set((state) => {
        const item = state.uploads[id];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- idempotent guard on unknown id (see updateProgress).
        if (!item) return state;
        // Terminal guard: a prior terminal transition wins.
        if (TERMINAL_STATES.includes(item.status)) return state;
        transitioned = true;
        return {
          uploads: {
            ...state.uploads,
            [id]: { ...item, status: 'success', progress: 100, completedAt: Date.now() },
          },
        };
      });
      // Schedule the auto-dismiss only when this call actually transitioned.
      // Skipping the schedule when the guard short-circuited prevents a
      // stale `success` timer from firing against an already-cancelled item.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated inside set() closure; TS control-flow doesn't model the mutation.
      if (transitioned) {
        get()._scheduleAutoDismiss(id, SUCCESS_DISMISS_MS);
      }
    },

    markFailed: (id: string, error: string): void => {
      set((state) => {
        const item = state.uploads[id];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- idempotent guard on unknown id (see updateProgress).
        if (!item) return state;
        // Terminal guard: user cancel or a prior success/failure wins.
        // Critical for the cancel→markFailed race: cancel() runs synchronously
        // and the axios CanceledError arrives a tick later — without this
        // guard the catch path would overwrite `cancelled` with `failed`.
        if (TERMINAL_STATES.includes(item.status)) return state;
        return {
          uploads: {
            ...state.uploads,
            [id]: { ...item, status: 'failed', error, completedAt: Date.now() },
          },
        };
      });
      // Failed items never auto-dismiss; the user has to dismiss them.
    },

    markCancelled: (id: string): void => {
      let transitioned = false;
      set((state) => {
        const item = state.uploads[id];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- idempotent guard on unknown id (see updateProgress).
        if (!item) return state;
        // Terminal guard: success/failed/already-cancelled win.
        if (TERMINAL_STATES.includes(item.status)) return state;
        transitioned = true;
        return {
          uploads: {
            ...state.uploads,
            [id]: { ...item, status: 'cancelled', completedAt: Date.now() },
          },
        };
      });
      // Schedule only when this call actually transitioned. Avoids
      // restarting the dismiss timer on a redundant cancel().
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated inside set() closure; TS control-flow doesn't model the mutation.
      if (transitioned) {
        get()._scheduleAutoDismiss(id, CANCELLED_DISMISS_MS);
      }
    },
  };
}

function makeDismissActions(set: SetState, get: GetState): DismissActions & InternalActions {
  return {
    _scheduleAutoDismiss: (id: string, delayMs: number): void => {
      const handle = sideTable.get(id);
      if (!handle) return;
      // `clearTimeout(null)` is a documented no-op per the DOM/Node spec,
      // so we can drop the `!== null` guard without changing behavior.
      // This avoids `@typescript-eslint/no-unnecessary-condition` against
      // the type-narrowed handle field while preserving the cancel-on-
      // reschedule semantic.
      clearTimeout(handle.dismissTimeout ?? undefined);
      handle.dismissTimeout = setTimeout(() => {
        get().dismiss(id);
      }, delayMs);
    },

    dismiss: (id: string): void => {
      sideTable.release(id);
      set((state) => {
        if (!(id in state.uploads)) return state;
        // Destructure-and-rest instead of `delete next[id]` so we don't
        // trip `@typescript-eslint/no-dynamic-delete`. Filter the entries
        // explicitly so the discarded value never gets bound to an unused
        // identifier (which would trip `no-unused-vars`).
        const rest: Record<string, UploadItem> = {};
        for (const [key, value] of Object.entries(state.uploads)) {
          if (key !== id) rest[key] = value;
        }
        return { uploads: rest };
      });
    },

    dismissAllTerminal: (): void => {
      const items = Object.values(get().uploads);
      for (const item of items) {
        if (item.status === 'success' || item.status === 'failed' || item.status === 'cancelled') {
          get().dismiss(item.id);
        }
      }
    },

    cancel: (id: string): void => {
      const handle = sideTable.get(id);
      handle?.abortController.abort();
      get().markCancelled(id);
    },
  };
}

export const useUploadQueueStore = create<UploadQueueStore>((set, get) => ({
  ...initialState,
  ...makeQueueActions(set),
  ...makeStatusActions(set, get),
  ...makeDismissActions(set, get),
}));
